import { z } from 'zod';
import { Ulid, UserIdentity } from './common.js';
import { Uuid } from './analytics.js';
import { EVENT_NAME_PATTERN, EVENT_STRING_PATTERN, GameEventPlayer, MAX_EVENT_NUMBER } from './game-events.js';
import { PSEUDONYM_ADJECTIVES, PSEUDONYM_ANIMALS } from './play-words.js';
import { UnlockTier } from './unlock-moment.js';
export { PSEUDONYM_ADJECTIVES, PSEUDONYM_ANIMALS } from './play-words.js';
/**
 * Play services — achievements and leaderboards (`docs/achievements-plan.md`,
 * `docs/leaderboards-plan.md`).
 *
 * The foundation both projects share (phase F0): the catalog a game declares in
 * `boardflight.play.json`, its limits, the envelopes the layer sends, the generated name a player
 * is shown under, and the linter. Like `game-events.ts`, several things that are not this module
 * have to agree on these rules — the Worker that validates a catalog and a batch of records, the
 * dashboard's two Settings pages, the SDK, the layer, agentd's tools and the CLI — so the rules
 * are written once, here.
 *
 * **On the word `play`.** It is Google's *Play Games Services* sense, and has nothing to do with
 * the library's play counts (`play_events`).
 */
/* --------------------------------------------------------------------------------- limits */
/**
 * An achievement's or a leaderboard's key: the events name rule (§5.1), and **permanent** once
 * anyone holds it, because players' records are keyed by it (§5.5).
 */
export const PLAY_KEY_PATTERN = EVENT_NAME_PATTERN;
/** §5.2. Game Center's ceiling, and more than any small Board game needs. */
export const MAX_ACHIEVEMENTS_PER_GAME = 100;
/** Each achievement's points: a multiple of five, five to a hundred (decision 8). */
export const ACHIEVEMENT_POINTS_STEP = 5;
export const MIN_ACHIEVEMENT_POINTS = 5;
export const MAX_ACHIEVEMENT_POINTS = 100;
/** A game's points, all achievements together: the shared convention, so a profile total means something. */
export const MAX_POINTS_PER_GAME = 1_000;
/** A progress achievement's `target`. */
export const MIN_ACHIEVEMENT_TARGET = 2;
export const MAX_ACHIEVEMENT_TARGET = 1_000_000;
export const MAX_LEADERBOARDS_PER_GAME = 20;
/** Boards counted with every variant combination: 20 × the 24-combination cap would be 480. */
export const MAX_BOARDS_PER_GAME = 200;
export const MAX_VARIANT_DIMENSIONS = 2;
export const MAX_VARIANT_COMBINATIONS = 24;
/** A stored catalog, serialized. Every layer reads it at startup (§8.4). */
export const MAX_PLAY_CATALOG_BYTES = 128 * 1024;
/** An achievement icon: square, this many pixels a side, PNG or WebP (§5.4). */
export const ACHIEVEMENT_ICON_PX = 512;
export const ACHIEVEMENT_ICON_MAX_BYTES = 512 * 1024;
/** The player-facing text (§5.1). */
export const PLAY_NAME_MAX = 40;
/** Xbox's limit; Apple truncates at two lines. Says literally how to earn it (§2.2). */
export const PLAY_DESCRIPTION_MIN = 10;
export const PLAY_DESCRIPTION_MAX = 100;
/** The payoff line shown once an achievement is unlocked, instead of its description. */
export const ACHIEVEMENT_EARNED_MAX = 100;
/** The reader's text: when the code fires it, from where, for whom (§5.3). Never shown to a player. */
export const PLAY_TRIGGER_MIN = 20;
export const PLAY_TRIGGER_MAX = 400;
/** Records in one `POST /v1/play/records` (§7.3, §8.2). */
export const MAX_PLAY_RECORDS_PER_REQUEST = 50;
/** Counted inside the game's `PlayStore`, where every record passes anyway, so it is exact (§8.2). */
export const MAX_PLAY_RECORDS_PER_INSTALL_PER_HOUR = 600;
/** Per game, per UTC day, counted in the object too. What it drops is shown as a fact, never with a reason. */
export const MAX_PLAY_RECORDS_PER_GAME_PER_DAY = 100_000;
/** Questions in one `POST /v1/play/query` (§8.3), and how many of them one install may ask an hour. */
export const MAX_PLAY_ASKS_PER_REQUEST = 8;
export const MAX_PLAY_QUERIES_PER_INSTALL_PER_HOUR = 1_200;
/** A `top` ask's page, and an `around` ask's reach either side. */
export const MAX_PLAY_TOP_LIMIT = 25;
export const MAX_PLAY_AROUND_RANGE = 5;
/** Successive play messages within this long go in one request (§7.3). */
export const PLAY_COALESCE_MS = 250;
/** Records the layer keeps unsent. Play records are never evicted to make room for custom events. */
export const PLAY_LAYER_QUEUE_MAX = 500;
/** `records` in a game's store: the idempotency and moderation trail (§14.3). Unlocks and entries stay. */
export const PLAY_RECORD_RETENTION_DAYS = 90;
/* -------------------------------------------------------------------------------- catalog */
const PlayKey = (what) => z
    .string()
    .regex(PLAY_KEY_PATTERN, `${what} key is lower snake case: a–z, 0–9 and _, up to 40 characters, starting with a letter.`);
const PlayName = z
    .string({ error: 'Every entry needs a name.' })
    .trim()
    .min(1, 'Every entry needs a name.')
    .max(PLAY_NAME_MAX, `A name is at most ${PLAY_NAME_MAX} characters.`);
/** The player's sentence: *Build 100 towers.* */
const PlayDescription = z
    .string({ error: 'Every entry needs a description: what a player does to earn it, in plain words.' })
    .trim()
    .min(PLAY_DESCRIPTION_MIN, `A description says how to earn it, in at least ${PLAY_DESCRIPTION_MIN} characters.`)
    .max(PLAY_DESCRIPTION_MAX, `A description is at most ${PLAY_DESCRIPTION_MAX} characters.`);
/**
 * The reader's sentence: *Incremented in TowerPlacer.place for the seat that placed it.* Required,
 * and **an agent proposing a set writes it itself**, from the code it read (decision 9).
 */
const PlayTrigger = z
    .string({ error: 'Every entry needs a trigger: when the code fires it, from where, and for whom.' })
    .trim()
    .min(PLAY_TRIGGER_MIN, `A trigger says when the code fires it, from where and for whom, in at least ${PLAY_TRIGGER_MIN} characters.`)
    .max(PLAY_TRIGGER_MAX, `A trigger is at most ${PLAY_TRIGGER_MAX} characters.`);
/** An identifier a player never reads as prose: a variant value (`"4"`, `"canyon"`). */
const PlayIdentifier = z
    .string()
    .regex(EVENT_STRING_PATTERN, 'Must be an identifier: letters, digits and _ . : - only, up to 48 characters.');
/**
 * An icon as the repo holds it: a path beside `boardflight.play.json`, PNG or WebP. Relative, with
 * no `..`, because every pusher resolves it against the catalog's own folder (§5.4).
 */
export const PlayIconPath = z
    .string()
    .regex(/^(?!\/)(?!.*(?:^|\/)\.\.(?:\/|$))[A-Za-z0-9_\-./]{1,196}\.(?:png|webp)$/, 'An icon is a relative path to a .png or .webp file beside the catalog.');
/**
 * An icon as the server holds it: an uploaded file of this game's, and its digest. Every pusher
 * uploads the files the server does not already have and sends this instead of the path (§5.4).
 */
export const PlayIconRef = z
    .object({
    media: Ulid,
    sha256: z.string().regex(/^[0-9a-f]{64}$/, 'A sha256 is 64 lowercase hex characters.'),
})
    .strict();
export const PlayIcon = z.union([PlayIconPath, PlayIconRef]);
/**
 * What kind of feat an achievement rewards (§2.2). Agents and the linter read it and the dashboard
 * groups by it; a player never sees the word.
 */
export const ACHIEVEMENT_CATEGORIES = ['progress', 'exploration', 'collection', 'skill', 'table', 'fun'];
export const AchievementCategory = z.enum(ACHIEVEMENT_CATEGORIES);
/** What each category is for, in one line — the editor's legend and the skill's table. */
export const ACHIEVEMENT_CATEGORY_SUMMARY = {
    progress: 'Getting through the game: the first win, the last chapter.',
    exploration: 'Finding things: a secret room, every map, a mode nobody tried.',
    collection: 'Gathering a set: every hero, a hundred towers.',
    skill: 'Doing something hard: a flawless round, a speedrun.',
    table: 'Playing together: a co-op clear, a full table, everyone scoring.',
    fun: 'A joke, a quirk or a surprise, usually hidden.',
};
export const AchievementDefinition = z
    .object({
    name: PlayName,
    description: PlayDescription,
    /** Shown once it is unlocked, instead of `description` — Xbox's and Apple's second description. */
    earned: z.string().trim().min(1).max(ACHIEVEMENT_EARNED_MAX, `An earned line is at most ${ACHIEVEMENT_EARNED_MAX} characters.`).optional(),
    trigger: PlayTrigger,
    points: z
        .number({ error: `Every achievement has points: a multiple of ${ACHIEVEMENT_POINTS_STEP}, ${MIN_ACHIEVEMENT_POINTS}–${MAX_ACHIEVEMENT_POINTS}.` })
        .int()
        .min(MIN_ACHIEVEMENT_POINTS, `At least ${MIN_ACHIEVEMENT_POINTS} points.`)
        .max(MAX_ACHIEVEMENT_POINTS, `At most ${MAX_ACHIEVEMENT_POINTS} points.`)
        .multipleOf(ACHIEVEMENT_POINTS_STEP, `Points are a multiple of ${ACHIEVEMENT_POINTS_STEP}.`),
    /** Required for any achievement on a channel that has achievements on (§5.1). */
    icon: PlayIcon.optional(),
    /** Concealed in the UI, not secret: the layer's copy of the catalog carries its text (§8.4). */
    hidden: z.boolean().optional(),
    /** Makes it a **progress** achievement, moved by `increment` and `set`: a bar, "37 / 100". */
    target: z
        .number()
        .int()
        .min(MIN_ACHIEVEMENT_TARGET, `A target is at least ${MIN_ACHIEVEMENT_TARGET}; one step is an ordinary unlock.`)
        .max(MAX_ACHIEVEMENT_TARGET)
        .optional(),
    category: AchievementCategory.optional(),
})
    .strict();
export const LeaderboardOrder = z.enum(['desc', 'asc']);
/** How a score is shown: a whole number with a unit, a decimal, or a time. */
export const LeaderboardFormat = z.discriminatedUnion('type', [
    z
        .object({
        type: z.literal('int'),
        /** An identifier, not prose: `wave`, `pts`, `moves`. */
        unit: z.string().regex(PLAY_KEY_PATTERN, 'A unit is an identifier: wave, pts, moves.').optional(),
    })
        .strict(),
    z.object({ type: z.literal('decimal'), decimals: z.number().int().min(1).max(3) }).strict(),
    z.object({ type: z.literal('time'), precision: z.enum(['s', 'ms']) }).strict(),
]);
/** Steam's keep-best and force-update, plus PlayFab's sum for "total coins ever" boards. */
export const LeaderboardKeep = z.enum(['best', 'latest', 'sum']);
/** Periods are UTC, and weeks start on Monday. */
export const LEADERBOARD_WINDOWS = ['all', 'month', 'week', 'day'];
export const LeaderboardWindow = z.enum(LEADERBOARD_WINDOWS);
/** A board of players, or of tables — a co-op team is the set of its members (§4.6). */
export const LeaderboardEntity = z.enum(['player', 'table']);
/**
 * Up to two dimensions, each a list of identifier values. Every combination is its own board:
 * `players` is the usual one on a Board, where a solo score and a four-player score are not the
 * same contest.
 */
export const LeaderboardVariants = z
    .record(PlayKey('A variant'), z.array(PlayIdentifier).min(1).max(MAX_VARIANT_COMBINATIONS))
    .superRefine((variants, ctx) => {
    const dimensions = Object.entries(variants);
    if (dimensions.length > MAX_VARIANT_DIMENSIONS) {
        ctx.addIssue({ code: 'custom', message: `At most ${MAX_VARIANT_DIMENSIONS} variant dimensions.` });
    }
    for (const [dimension, values] of dimensions) {
        const repeated = values.find((value, index) => values.indexOf(value) !== index);
        if (repeated !== undefined) {
            ctx.addIssue({ code: 'custom', path: [dimension], message: `"${repeated}" is listed twice.` });
        }
    }
    const combinations = variantCombinations(variants);
    if (combinations > MAX_VARIANT_COMBINATIONS) {
        ctx.addIssue({
            code: 'custom',
            message: `${combinations} combinations; a board has at most ${MAX_VARIANT_COMBINATIONS}.`,
        });
    }
});
/**
 * Required (§11): a submission outside them is refused and counted. A wide range is fine; a
 * missing one means a cheater's 2³¹ sits at the top until somebody notices.
 */
export const LeaderboardBounds = z
    .object({
    min: z.number().finite().min(-MAX_EVENT_NUMBER).max(MAX_EVENT_NUMBER),
    max: z.number().finite().min(-MAX_EVENT_NUMBER).max(MAX_EVENT_NUMBER),
}, {
    error: (issue) => issue.input === undefined
        ? 'Every leaderboard needs bounds: the lowest and highest score that can really happen.'
        : 'Bounds are { "min": …, "max": … }.',
})
    .strict()
    .refine((bounds) => bounds.max > bounds.min, { message: 'max must be greater than min.', path: ['max'] });
export const LeaderboardDefinition = z
    .object({
    name: PlayName,
    description: PlayDescription,
    trigger: PlayTrigger,
    order: LeaderboardOrder,
    format: LeaderboardFormat,
    keep: LeaderboardKeep.optional(),
    windows: z
        .array(LeaderboardWindow, { error: 'A board needs at least one window: all, month, week or day.' })
        .min(1, 'A board needs at least one window: all, month, week or day.')
        .max(LEADERBOARD_WINDOWS.length)
        .refine((windows) => new Set(windows).size === windows.length, 'A window is listed twice.'),
    entity: LeaderboardEntity.optional(),
    variants: LeaderboardVariants.optional(),
    bounds: LeaderboardBounds,
})
    .strict();
/**
 * The set's style sheet (§13.5), for the next agent asked to add one icon that matches the other
 * fourteen. Nothing reads it but agents and people.
 */
export const PlayArt = z
    .object({
    style: z.string().trim().min(1).max(400).optional(),
    palette: z.array(z.string().regex(/^#[0-9a-fA-F]{6}$/, 'A colour is #rrggbb.')).min(1).max(8).optional(),
    background: z.string().trim().min(1).max(200).optional(),
    frame: z.string().trim().min(1).max(200).optional(),
})
    .strict();
/** The combinations of one board's variants: 1 for a board with none. */
export function variantCombinations(variants) {
    return Object.values(variants ?? {}).reduce((product, values) => product * Math.max(1, values.length), 1);
}
/**
 * `boardflight.play.json` (§5.1), and what `PUT /v1/apps/:appId/play-catalog` stores.
 *
 * Both halves are one file and one version: the Achievements and Leaderboards pages each edit
 * theirs and write the whole. Unknown keys are refused rather than ignored, everywhere but
 * `$schema`, as in the event catalog — a `"triger"` silently ignored would be an entry without a
 * trigger that looked like it had one.
 */
export const PlayCatalog = z
    .object({
    $schema: z.string().max(200).optional(),
    achievements: z
        .record(PlayKey('An achievement'), AchievementDefinition)
        .refine((entries) => Object.keys(entries).length <= MAX_ACHIEVEMENTS_PER_GAME, `At most ${MAX_ACHIEVEMENTS_PER_GAME} achievements.`)
        .default({}),
    leaderboards: z
        .record(PlayKey('A leaderboard'), LeaderboardDefinition)
        .refine((entries) => Object.keys(entries).length <= MAX_LEADERBOARDS_PER_GAME, `At most ${MAX_LEADERBOARDS_PER_GAME} leaderboards.`)
        .default({}),
    ui: z
        .object({
        /** A *New best* card for a score improvement. Off unless asked: most games already shout about one. */
        scoreToasts: z.boolean().optional(),
    })
        .strict()
        .optional(),
    art: PlayArt.optional(),
})
    .strict()
    .superRefine((catalog, ctx) => {
    const points = playCatalogPoints(catalog);
    if (points > MAX_POINTS_PER_GAME) {
        ctx.addIssue({
            code: 'custom',
            path: ['achievements'],
            message: `The points add up to ${points.toLocaleString('en-US')}; a game has at most ${MAX_POINTS_PER_GAME.toLocaleString('en-US')}.`,
        });
    }
    const boards = playBoardCount(catalog);
    if (boards > MAX_BOARDS_PER_GAME) {
        ctx.addIssue({
            code: 'custom',
            path: ['leaderboards'],
            message: `${boards} boards counting every variant; a game has at most ${MAX_BOARDS_PER_GAME}.`,
        });
    }
});
/** The URL `"$schema"` names, and where the published schema is served. */
export const PLAY_SCHEMA_URL = 'https://boardflight.app/schemas/play-v1.json';
/** The points of every achievement, together. The budget meter's number. */
export function playCatalogPoints(catalog) {
    return Object.values(catalog.achievements ?? {}).reduce((sum, entry) => sum + (typeof entry.points === 'number' && Number.isFinite(entry.points) ? entry.points : 0), 0);
}
/** Every board, counting each variant combination as one. */
export function playBoardCount(catalog) {
    return Object.values(catalog.leaderboards ?? {}).reduce((sum, board) => sum + variantCombinations(board.variants), 0);
}
/**
 * `boardflight.play.json`'s published JSON Schema, served at `PLAY_SCHEMA_URL` so editors and
 * agents validate as they type (§5.1). Generated from `PlayCatalog` itself, so the file and the
 * server's check cannot disagree; `apps/web/test/play-schema.test.ts` fails when the committed
 * copy is stale. Refinements — the points budget, the board count, the variant cap — are the
 * server's alone; a schema cannot say them.
 */
export function playCatalogJsonSchema() {
    return {
        ...z.toJSONSchema(PlayCatalog, { io: 'input', unrepresentable: 'any' }),
        $id: PLAY_SCHEMA_URL,
        title: 'BoardFlight achievements and leaderboards catalog',
        description: "A Board game's achievements and leaderboards, fired through @boardflight/game. Every entry needs a player-facing description and a developer-facing trigger: when the code fires it, from where and for whom (docs/achievements-plan.md §5).",
    };
}
/** The catalog a game with none has. */
export const EMPTY_PLAY_CATALOG = { achievements: {}, leaderboards: {} };
/**
 * A refused catalog's problems, one sentence each, naming the entry — "Achievement first_line:
 * trigger — Every entry needs a trigger…" rather than a zod path, because the reader is as often
 * an agent as a person. `PUT …/play-catalog` puts these in its 400.
 */
export function playCatalogProblems(error) {
    return error.issues.map((issue) => {
        // Inside a request body the catalog sits under `catalog`; the reader cares about the entry.
        const raw = issue.path.map(String);
        const path = raw[0] === 'catalog' ? raw.slice(1) : raw;
        // A malformed key arrives as "Invalid key in record", with the key's own rule nested inside.
        const message = issue.code === 'invalid_key' ? (issue.issues[0]?.message ?? issue.message) : issue.message;
        const half = path[0] === 'achievements' ? 'Achievement' : path[0] === 'leaderboards' ? 'Leaderboard' : null;
        if (half && path.length >= 2) {
            const rest = path.slice(2).join('.');
            return rest ? `${half} ${path[1]}, ${rest}: ${message}` : `${half} ${path[1]}: ${message}`;
        }
        return path.length > 0 ? `${path.join('.')}: ${message}` : message;
    });
}
/** Fewer than this many achievements and a set is too thin to pace (§2.2); more and it is grind. */
export const PLAY_SET_MIN = 5;
export const PLAY_SET_MAX = 50;
/** An achievement worth this or less is a first-session one; a set wants at least three. */
export const PLAY_EASY_POINTS = 15;
export const PLAY_EASY_MIN = 3;
/** Words that say nothing about an entry beyond its name. */
const FILLER = new Set([
    'a', 'an', 'the', 'is', 'are', 'was', 'be', 'when', 'this', 'that', 'it', 'its', 'of', 'for', 'on',
    'in', 'to', 'at', 'by', 'and', 'or', 'you', 'your', 'player', 'players', 'game', 'achievement',
    'achievements', 'leaderboard', 'board', 'unlock', 'unlocks', 'unlocked', 'earn', 'earns',
    'earned', 'get', 'gets', 'got', 'fired', 'fires', 'fire', 'triggered', 'trigger', 'triggers',
    'sent', 'send', 'submitted', 'submit', 'happens', 'awarded', 'award', 'given', 'score', 'scores',
]);
/**
 * Words that move a rating past PEGI 12 / ESRB E10+ when a player reads them on a trophy (§5.6).
 * Advice for the team about its own text, not a moderation filter: it warns and never refuses.
 */
const NOT_FOR_TWELVE = new Set([
    'fuck', 'fucking', 'fucked', 'shit', 'shitty', 'bitch', 'bastard', 'cunt', 'dick', 'cock', 'pussy',
    'whore', 'slut', 'piss', 'pissed', 'asshole', 'arse', 'arsehole', 'wanker', 'twat', 'porn', 'sex',
    'sexy', 'nude', 'naked', 'boobs', 'cocaine', 'heroin', 'meth', 'weed', 'stoned', 'drunk', 'booze',
    'gore', 'gory', 'behead', 'beheaded', 'decapitate', 'decapitated', 'dismember', 'dismembered',
    'torture', 'tortured', 'suicide', 'rape', 'raped', 'nazi', 'retard', 'retarded',
]);
const words = (text) => text.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
/** A function, a method, a component or a constant: `TowerPlacer.place`, `onWaveCleared`, `wave_end()`. */
const CODE_LIKE = /[A-Za-z_$][\w$]*\.[A-Za-z_$][\w$]*|\b[a-z][a-z0-9]*[A-Z][A-Za-z0-9]*\b|\b[A-Z][a-z0-9]+[A-Z][A-Za-z0-9]*\b|\b\w+\(\)|\b[a-z0-9]+_[a-z0-9_]+\b/;
/** A trigger that says why its achievement is hidden: a spoiler, a joke, a discovery. */
const HIDING_REASON = /\b(hidden|hide|hides|hiding|spoil\w*|secret\w*|surpris\w*|joke\w*|discover\w*|reveal\w*|twist|easter)\b/i;
/**
 * Whether a trigger names a function, a screen or a moment (§5.3), as far as a linter can tell:
 * something code-shaped, or at least three words of its own that the name and description do not
 * already say. *"When the player gets it."* fails; *"Fired at wave end for each seat…"* passes.
 */
function triggerNamesSomething(trigger, name, description) {
    if (CODE_LIKE.test(trigger))
        return true;
    const said = new Set([...words(name), ...words(description), ...FILLER]);
    return new Set(words(trigger).filter((word) => !said.has(word))).size >= 3;
}
function restatesName(name, key, description) {
    const known = new Set([...words(name), ...words(key), ...FILLER]);
    return words(description).every((word) => known.has(word));
}
function notForTwelve(text) {
    return words(text).find((word) => NOT_FOR_TWELVE.has(word)) ?? null;
}
/**
 * The linter the dashboard, the CLI and agentd's `play_check` all run (§5.6). It cannot judge
 * prose, so everything here is a warning: a catalog it complains about still saves.
 *
 * What it cannot see is left to the places that can: whether an icon is square is measured when
 * it is uploaded, and the pacing rules (three in a first session, at most one in the first five
 * minutes) are the channel report's, after launch.
 */
export function lintPlayCatalog(catalog) {
    const warnings = [];
    const achievements = Object.entries(catalog.achievements ?? {});
    const entryText = (section, key, entry) => {
        const label = section === 'achievements' ? `Achievement ${key}` : `Leaderboard ${key}`;
        if (restatesName(entry.name, key, entry.description)) {
            warnings.push({
                section,
                key,
                code: 'description_restates_name',
                message: `${label}: the description only restates the name. Say exactly what a player does to earn it.`,
            });
        }
        if (!triggerNamesSomething(entry.trigger, entry.name, entry.description)) {
            warnings.push({
                section,
                key,
                code: 'trigger_names_nothing',
                message: `${label}: the trigger names no function, screen or moment. Say where in the code it fires, when, and for which seats.`,
            });
        }
        const word = notForTwelve(entry.name) ?? notForTwelve(entry.description);
        if (word) {
            warnings.push({
                section,
                key,
                code: 'not_for_twelve',
                message: `${label}: "${word}" is not a word for a game a twelve-year-old plays. Players read the name and the description.`,
            });
        }
    };
    for (const [key, entry] of achievements) {
        entryText('achievements', key, entry);
        if (entry.icon === undefined) {
            warnings.push({
                section: 'achievements',
                key,
                code: 'icon_missing',
                message: `Achievement ${key}: no icon. Every achievement needs one before achievements are switched on for a channel.`,
            });
        }
        if (entry.hidden && !HIDING_REASON.test(entry.trigger)) {
            warnings.push({
                section: 'achievements',
                key,
                code: 'hidden_without_reason',
                message: `Achievement ${key} is hidden, and its trigger does not say why. Hide a spoiler or a joke, never a hard one.`,
            });
        }
    }
    for (const [key, entry] of Object.entries(catalog.leaderboards ?? {}))
        entryText('leaderboards', key, entry);
    const count = achievements.length;
    if (count > 0 && count < PLAY_SET_MIN) {
        warnings.push({
            section: 'achievements',
            key: null,
            code: 'set_small',
            message: `${count} achievement${count === 1 ? '' : 's'}. A set of fewer than ${PLAY_SET_MIN} gives a player little to aim for after the first session.`,
        });
    }
    if (count > PLAY_SET_MAX) {
        warnings.push({
            section: 'achievements',
            key: null,
            code: 'set_large',
            message: `${count} achievements. More than ${PLAY_SET_MAX} turns a set into a checklist; keep the ones that reward a feat.`,
        });
    }
    if (count >= 2 && achievements.every(([, entry]) => entry.target !== undefined)) {
        warnings.push({
            section: 'achievements',
            key: null,
            code: 'all_progress',
            message: 'Every achievement is a progress bar. Mix in single deeds that land at a moment, like a first win or a clever move.',
        });
    }
    const hidden = achievements.filter(([, entry]) => entry.hidden).length;
    const hiddenAllowed = Math.max(2, Math.floor(count * 0.15));
    if (hidden > hiddenAllowed) {
        warnings.push({
            section: 'achievements',
            key: null,
            code: 'too_many_hidden',
            message: `${hidden} of ${count} achievements are hidden. Keep it to ${hiddenAllowed}: a list of question marks tells a player nothing to try.`,
        });
    }
    const easy = achievements.filter(([, entry]) => typeof entry.points === 'number' && entry.points <= PLAY_EASY_POINTS).length;
    if (count >= PLAY_EASY_MIN && easy < PLAY_EASY_MIN) {
        warnings.push({
            section: 'achievements',
            key: null,
            code: 'few_easy',
            message: `Only ${easy} achievement${easy === 1 ? '' : 's'} worth ${PLAY_EASY_POINTS} points or less. A first session wants about three it can earn.`,
        });
    }
    return warnings;
}
/* ---------------------------------------------------------------------------- catalog API */
/** Who wrote a catalog version. The uploader's word, like a version name. */
export const PlayCatalogSource = z.enum(['dashboard', 'cli', 'mcp', 'playground', 'unity']);
export const PutPlayCatalogRequest = z.object({
    catalog: PlayCatalog,
    source: PlayCatalogSource.default('dashboard'),
    /**
     * The version the writer started from. Last write wins regardless (§5.5); this only lets the
     * response say what was replaced when somebody else wrote in between.
     */
    baseVersion: z.number().int().min(0).nullable().optional(),
});
export const PlayCatalogVersion = z.object({
    version: z.number().int(),
    source: PlayCatalogSource,
    author: UserIdentity.nullable(),
    createdAt: z.number().int(),
    achievements: z.number().int(),
    leaderboards: z.number().int(),
});
export const PlayCatalogResponse = z.object({
    appId: Ulid,
    /** The current catalog, or the requested version's. A game with none has both halves empty. */
    catalog: PlayCatalog,
    /** Null for a game that has never had one. */
    version: z.number().int().nullable(),
    /** The linter's view of `catalog`. */
    warnings: z.array(z.object({
        section: z.enum(['achievements', 'leaderboards']).nullable(),
        key: z.string().nullable(),
        code: z.string(),
        message: z.string(),
    })),
    /** Newest first, at most 50. */
    versions: z.array(PlayCatalogVersion),
    /** On a write: the version this one replaced, when it was not the writer's `baseVersion`. */
    replaced: PlayCatalogVersion.nullable().optional(),
    /** On a write: the catalog was identical to the current one, so no version was made. */
    unchanged: z.boolean().optional(),
});
/**
 * One stable serialization of a **parsed** catalog, so "identical to the current version" is a
 * string comparison. Not key-sorted, as the event catalog's is not: the writer's order of
 * achievements is the order a trophy room lists them, so reordering is a change and makes a
 * version.
 */
export function canonicalPlayCatalog(catalog) {
    return JSON.stringify(PlayCatalog.parse(catalog));
}
/* ---------------------------------------------------------------------------- the envelope */
/** What a play record does: three to an achievement, one to a leaderboard (§7.2). */
export const PlayOp = z.enum(['unlock', 'increment', 'set', 'submit']);
/**
 * One record as the layer sends it (§8.2). A seat is the events envelope's — a hashed per-game key,
 * a type and a position, never Board's id and never a name (§4.1).
 *
 * Parsed **one at a time**, as game events are: a record that fails here gets `invalid` and the
 * rest of its batch is kept.
 */
export const PlayRecord = z
    .object({
    /** The install's shared counter (§7.3): with `installId`, what makes a retried batch change nothing. */
    seq: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
    /** Device clock, ms. Clamped at ingest, as events are. */
    at: z.number().int().min(0),
    op: PlayOp,
    /** The achievement's or the leaderboard's key. */
    id: z.string().regex(PLAY_KEY_PATTERN),
    player: GameEventPlayer.optional(),
    /** For a board whose entity is `table`: every human seat at it. */
    players: z.array(GameEventPlayer).min(1).max(16).optional(),
    value: z.number().finite().min(-MAX_EVENT_NUMBER).max(MAX_EVENT_NUMBER).optional(),
    /** `{ dim: value }`, one value per declared dimension. */
    variant: z.record(z.string().regex(PLAY_KEY_PATTERN), PlayIdentifier).optional(),
})
    .strict();
/** `POST /v1/play/records` (§8.2). Its head is the events envelope's. */
export const PlayRecordsRequest = z.object({
    /** The game's ingest key, baked into the build. An identifier, not a credential. */
    key: z.string().min(1).max(200),
    buildId: Ulid,
    installId: Uuid,
    sessionId: Uuid.optional(),
    platform: z.enum(['unity', 'web']),
    /** The channel slug the build was stamped with. Compared with the build row, never trusted. */
    channelStamp: z.string().max(64).nullable().optional(),
    records: z.array(z.unknown()).min(1).max(MAX_PLAY_RECORDS_PER_REQUEST),
    /** The device's clock when this was sent, on the records' clock, so a skewed batch can be moved. */
    sentAt: z.number().int().optional(),
});
/**
 * One record's result (§8.2), in record order.
 *
 * A record whose `(installId, seq)` the game's store already holds is answered with **the result
 * it was given the first time**, so a layer retrying a batch it never heard back about learns what
 * it would have learnt then, and nothing changes twice. `duplicate` is the second copy of one seq
 * inside a single batch, which has no first answer to replay.
 *
 * Three were settled by building the route (F1), beside the plan's list:
 *  - `invalid` — the record did not parse, or lacks what its op needs (a player, a value): an SDK
 *    out of step with the server.
 *  - `disabled` — the channel has that service off while the other is on. Dropped, not kept.
 *  - `limited` — past the install's hourly or the game's daily budget, which only the game's store
 *    can count exactly. Dropped, not kept, and counted on the day's health line.
 */
export const PlayRecordResult = z.enum([
    'ok',
    'unknown_id',
    'guest',
    'ai',
    'out_of_bounds',
    'not_progress',
    'bad_variant',
    'duplicate',
    'invalid',
    'disabled',
    'limited',
]);
/**
 * One record's answer, in record order. The layer reads it, which is new for an ingest route.
 *
 * An achievement record that is `ok` carries what the store made of it (A1): `progress` for a
 * progress achievement, and — **only the first time this player has it** — `unlocked: true` with
 * the tier the unlock moment plays at and the rarity its card shows (§7.4.3). Nothing is said about
 * a record that unlocked nothing, so a game firing `unlock` every time its condition holds hears
 * the moment once.
 */
export const PlayRecordOutcome = z.object({
    result: PlayRecordResult,
    /** The first time this player has it. */
    unlocked: z.boolean().optional(),
    /** A progress achievement's count after this record. */
    progress: z.number().int().optional(),
    /** With `unlocked`: which of the moment's four sizes plays (`unlockTier`). */
    tier: UnlockTier.optional(),
    /**
     * With `unlocked`: the share of the `public` channel's players who hold it, after this record,
     * or `null` while fewer than `RARITY_MIN_PLAYERS` have played (§9.5).
     */
    rarity: z.number().min(0).max(1).nullable().optional(),
    /** A submission's best, whether it improved, and its rank in each window. */
    best: z.number().optional(),
    improved: z.boolean().optional(),
    ranks: z.partialRecord(LeaderboardWindow, z.number().int().min(1)).optional(),
});
export const PlayRecordsResponse = z.object({ results: z.array(PlayRecordOutcome) });
/** A seat's hashed key, as an ask names one. The same 64 hex the layer sent. */
const PlayerKey = z.string().regex(/^[0-9a-f]{64}$/);
/** The SDK's name for one question, echoed on its answer. */
const AskId = z.string().min(1).max(64);
const BoardAsk = {
    q: AskId,
    board: z.string().regex(PLAY_KEY_PATTERN),
    window: LeaderboardWindow,
    variant: z.record(z.string().regex(PLAY_KEY_PATTERN), PlayIdentifier).optional(),
};
/** One question a game asks about its own game (§8.3). Reads follow the asking build's channel. */
export const PlayAsk = z.discriminatedUnion('kind', [
    z.object({ q: AskId, kind: z.literal('achievements'), players: z.array(PlayerKey).min(1).max(16) }).strict(),
    z
        .object({
        ...BoardAsk,
        kind: z.literal('top'),
        limit: z.number().int().min(1).max(MAX_PLAY_TOP_LIMIT).optional(),
        after: z.string().max(200).optional(),
    })
        .strict(),
    z
        .object({ ...BoardAsk, kind: z.literal('around'), player: PlayerKey, range: z.number().int().min(1).max(MAX_PLAY_AROUND_RANGE).optional() })
        .strict(),
    /** Entries whose install is the asking one: "this Board's best". */
    z.object({ ...BoardAsk, kind: z.literal('local') }).strict(),
    /** Whether a seat is linked, and its username if its holder shows it. */
    z.object({ q: AskId, kind: z.literal('profile'), player: PlayerKey }).strict(),
]);
/** `POST /v1/play/query` (§8.3). The same ingest key and install as the records route. */
export const PlayQueryRequest = z.object({
    key: z.string().min(1).max(200),
    buildId: Ulid,
    installId: Uuid,
    asks: z.array(PlayAsk).min(1).max(MAX_PLAY_ASKS_PER_REQUEST),
});
/**
 * One answer per ask, by its `q`. `null` is every answer this server cannot give yet — and, by the
 * SDK's rule (§6.1), every answer it will not give: `null` never says why.
 */
export const PlayQueryResponse = z.object({
    answers: z.array(z.object({ q: z.string(), answer: z.unknown().nullable() })),
});
/**
 * The answer to `{ kind: 'achievements', players }` (§8.3, §6.5): every achievement this game
 * declares, with its rarity, and each asked-for seat's state in it. Keyed by the same per-game codes
 * the layer sent, which are no secret on this path (§8.3). An achievement a seat has never touched
 * is absent from its map rather than listed at zero.
 */
export const PlayAchievementsAnswer = z.object({
    achievements: z.record(z.string(), z.object({ rarity: z.number().min(0).max(1).nullable() })),
    players: z.record(z.string(), z.record(z.string(), z.object({ progress: z.number().int(), unlockedAt: z.number().int().nullable() }))),
});
/**
 * An achievement a catalog write removed while somebody holds it (§5.5). It is **retired**, not
 * gone: hidden from lists, kept by everybody who has it, and restored by re-adding the same key.
 * `holders` counts distinct players on every channel of the game.
 */
export const PlayCatalogRetired = z.object({ key: z.string(), holders: z.number().int().min(1) });
/**
 * What `PUT …/play-catalog` answers: the catalog as saved, and every achievement the write retired
 * with how many hold it — empty when it retired none. Last write wins regardless; this is what lets
 * the dashboard and agentd's deploy say what a push cost the players who had something (§5.5).
 */
export const PlayCatalogWriteResponse = PlayCatalogResponse.extend({
    retired: z.array(PlayCatalogRetired).optional(),
});
/* ---------------------------------------------------------------------------- the icons' sizes */
/**
 * The renditions an achievement icon's finalize writes beside the original, as WebP (§5.4): the
 * layer's card and every list read the small one, a profile's showcase the larger.
 */
export const ACHIEVEMENT_ICON_RENDITIONS = [128, 256];
/**
 * Where an uploaded icon is served: the original at `/v1/media/<id>`, a rendition at
 * `/v1/media/<id>/<px>`. Public and immutable, as every media URL is.
 */
export function achievementIconUrl(apiBase, mediaId, size) {
    const base = `${apiBase.replace(/\/+$/, '')}/v1/media/${mediaId}`;
    return size === undefined ? base : `${base}/${size}`;
}
/**
 * The smallest icon finalize accepts, a side: the larger rendition, so neither is ever scaled up.
 * A file this size or bigger is measured and refused when it is not square (§5.4).
 */
export const ACHIEVEMENT_ICON_MIN_PX = 256;
/**
 * One of a game's uploaded achievement icons, as `GET /v1/apps/:appId/achievement-icons` lists it
 * and an icon's finalize answers. Width and height are **measured**, which no store image's are.
 * Every pusher reads the list before it uploads and sends only what is not on it by `sha256` —
 * content-addressed, so an unchanged icon is never sent twice — then names each in the catalog as
 * `{ media: mediaId, sha256 }` (§5.4).
 */
export const AchievementIcon = z.object({
    mediaId: Ulid,
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    width: z.number().int(),
    height: z.number().int(),
    contentType: z.string(),
    size: z.number().int(),
    /** The original, as uploaded. */
    url: z.string(),
    /** The WebP renditions, by side in pixels. */
    renditions: z.object({ '128': z.string(), '256': z.string() }),
});
export const AchievementIconsResponse = z.object({ appId: Ulid, icons: z.array(AchievementIcon) });
/* ------------------------------------------------------------------------ checking a record */
/** The two services a channel switches, one column each (§8.1). */
export const PLAY_SERVICES = ['achievements', 'leaderboards'];
/** Which ops belong to which service. */
export const ACHIEVEMENT_OPS = ['unlock', 'increment', 'set'];
/** A variant, written the one way the store keys a board by (§9.3). */
export function canonicalVariant(variant) {
    return Object.keys(variant ?? {})
        .sort()
        .map((dimension) => `${dimension}:${variant[dimension]}`)
        .join(';');
}
/**
 * What the records route makes of one parsed record (§8.2), before the store sees it: the catalog
 * says whether the id is real and what it takes, the channel says whether its service is on, and
 * the seat says whether anybody can keep it. Pure, so the playground's dev sink can give a game the
 * answer a Board would get.
 *
 * **Self-contained**, and it has to stay so: the preview harness inlines this function's own source
 * into the dev sink at serve time (`playground/harness/vite.harness.mjs`), as it inlines agentd's
 * scene serializer, so nothing here may reach a name outside the function. A test runs the source
 * alone to hold it to that.
 *
 * The store decides the rest — whether `(installId, seq)` was seen and whether a budget is spent.
 */
export function checkPlayRecord(record, catalog, on) {
    // `canonicalVariant`, written again here because nothing outside this function may be reached.
    const given = record.variant ?? {};
    const variant = Object.keys(given)
        .sort()
        .map((dimension) => `${dimension}:${given[dimension]}`)
        .join(';');
    const answer = (result, extra = {}) => ({
        result,
        playerKey: null,
        members: null,
        value: record.value ?? null,
        variant,
        ...extra,
    });
    // A seat nobody can keep. An AI never unlocks or ranks; a guest's key lasts one session (§4.6).
    const seat = (player) => {
        if (!player)
            return 'invalid';
        if (player.t === 'ai')
            return 'ai';
        if (player.t === 'guest')
            return 'guest';
        return player.k ? null : 'invalid';
    };
    if (record.op === 'unlock' || record.op === 'increment' || record.op === 'set') {
        const entry = catalog.achievements[record.id];
        if (!on.achievements)
            return answer('disabled');
        if (!entry)
            return answer('unknown_id');
        if (record.players !== undefined)
            return answer('invalid');
        const refused = seat(record.player);
        if (refused)
            return answer(refused);
        if (record.op === 'increment' && !(Number.isInteger(record.value) && record.value >= 1))
            return answer('invalid');
        if (record.op === 'set' && !(Number.isInteger(record.value) && record.value >= 0))
            return answer('invalid');
        if (record.op !== 'unlock' && entry.target === undefined)
            return answer('not_progress');
        return answer('ok', { playerKey: record.player.k, value: record.op === 'unlock' ? null : record.value });
    }
    const board = catalog.leaderboards[record.id];
    if (!on.leaderboards)
        return answer('disabled');
    if (!board)
        return answer('unknown_id');
    if (typeof record.value !== 'number')
        return answer('invalid');
    if (board.format.type === 'int' && !Number.isInteger(record.value))
        return answer('invalid');
    let playerKey = null;
    let members = null;
    if ((board.entity ?? 'player') === 'player') {
        if (record.players !== undefined)
            return answer('invalid');
        const refused = seat(record.player);
        if (refused)
            return answer(refused);
        playerKey = record.player.k;
    }
    else {
        // A table is its members. An AI seat is no member; a guest is one, under its session's key, so
        // a table with a guest still ranks (§4.6).
        const seats = record.players ?? (record.player ? [record.player] : []);
        if (seats.length === 0)
            return answer('invalid');
        const people = seats.filter((member) => member.t !== 'ai');
        if (people.length === 0)
            return answer('ai');
        if (people.some((member) => !member.k))
            return answer('invalid');
        members = [...new Set(people.map((member) => member.k))].sort();
    }
    if (record.value < board.bounds.min || record.value > board.bounds.max)
        return answer('out_of_bounds');
    const declared = board.variants ?? {};
    const dimensions = Object.keys(declared);
    const fits = Object.keys(given).length === dimensions.length &&
        dimensions.every((dimension) => given[dimension] !== undefined && declared[dimension].includes(given[dimension]));
    if (!fits)
        return answer('bad_variant');
    return answer('ok', { playerKey, members, value: record.value });
}
/* ---------------------------------------------------------------- the switches, and the layer */
/** One channel's two switches, as `GET /v1/apps/:appId/play-settings` lists them. */
export const PlayChannelSettings = z.object({
    channelId: Ulid,
    slug: z.string(),
    name: z.string(),
    achievements: z.boolean(),
    leaderboards: z.boolean(),
});
export const PlaySettingsResponse = z.object({ appId: Ulid, channels: z.array(PlayChannelSettings) });
/** `PUT /v1/apps/:appId/play-settings`. An absent switch is left alone. */
export const PutPlaySettingsRequest = z
    .object({
    channelId: Ulid,
    achievements: z.boolean().optional(),
    leaderboards: z.boolean().optional(),
})
    .strict();
/**
 * The layer config's `play` block (§8.4): the kill switch for builds already on Boards, and the
 * catalog version, so a layer re-reads its copy of the catalog only when it moved. Each service is
 * its channel's switch **and** the team's key — a team that loses the key goes quiet everywhere.
 */
export const PlayLayerConfig = z.object({
    achievements: z.boolean(),
    leaderboards: z.boolean(),
    catalogVersion: z.number().int().nullable(),
});
/**
 * `GET /v1/play/catalog` — what a layer needs to draw and answer: the player-facing text, points,
 * flags and icon URLs, and each board's shape. Never a `trigger`: that sentence is the team's and
 * an agent's, and it names the code. Hidden achievements' text is here, as on every platform —
 * hidden is concealed in the UI, not secret (§8.4).
 */
export const PlayLayerCatalog = z.object({
    version: z.number().int().nullable(),
    achievements: z.record(z.string(), z.object({
        name: z.string(),
        description: z.string(),
        earned: z.string().optional(),
        points: z.number().int(),
        hidden: z.boolean(),
        target: z.number().int().optional(),
        /**
         * An uploaded icon's 128 px rendition (`achievementIconUrl`); null for none, and for a path the
         * server cannot serve.
         */
        icon: z.string().nullable(),
        /**
         * The share of the `public` channel's players who hold it when the catalog was read, or `null`
         * below `RARITY_MIN_PLAYERS` (§9.5). The moment reads a record's own answer first; this is what
         * it falls back on offline.
         */
        rarity: z.number().min(0).max(1).nullable(),
    })),
    leaderboards: z.record(z.string(), z.object({
        name: z.string(),
        description: z.string(),
        order: LeaderboardOrder,
        format: LeaderboardFormat,
        keep: LeaderboardKeep,
        windows: z.array(LeaderboardWindow),
        entity: LeaderboardEntity,
        variants: z.record(z.string(), z.array(z.string())).optional(),
    })),
    ui: z.object({ scoreToasts: z.boolean() }),
});
/**
 * A stored catalog, as a layer gets it. `iconUrl` turns an uploaded icon's id into its URL, and
 * `rarity` is each achievement's public share, where the store has one.
 */
export function playLayerCatalog(catalog, version, iconUrl, rarity = {}) {
    return {
        version,
        achievements: Object.fromEntries(Object.entries(catalog.achievements).map(([key, entry]) => [
            key,
            {
                name: entry.name,
                description: entry.description,
                ...(entry.earned !== undefined ? { earned: entry.earned } : {}),
                points: entry.points,
                hidden: entry.hidden === true,
                ...(entry.target !== undefined ? { target: entry.target } : {}),
                icon: entry.icon && typeof entry.icon === 'object' ? iconUrl(entry.icon.media) : null,
                rarity: rarity[key] ?? null,
            },
        ])),
        leaderboards: Object.fromEntries(Object.entries(catalog.leaderboards).map(([key, board]) => [
            key,
            {
                name: board.name,
                description: board.description,
                order: board.order,
                format: board.format,
                keep: board.keep ?? 'best',
                windows: board.windows,
                entity: board.entity ?? 'player',
                ...(board.variants ? { variants: board.variants } : {}),
            },
        ])),
        ui: { scoreToasts: catalog.ui?.scoreToasts === true },
    };
}
/** One UTC day of a game's records, by result: the health line (§12.3). */
export const PlayHealthDay = z.object({
    day: z.string(),
    results: z.partialRecord(PlayRecordResult, z.number().int()),
});
/** `GET /v1/apps/:appId/play/health`: the days that had records, newest first, and the last one's time. */
export const PlayHealthResponse = z.object({
    appId: Ulid,
    channelId: Ulid.nullable(),
    days: z.array(PlayHealthDay),
    lastAt: z.number().int().nullable(),
});
/* ------------------------------------------------------------------------- the generated name */
/** 32-bit FNV-1a, then murmur3's finalizer so every input bit moves every output bit. */
function hash32(text, seed) {
    let hash = (0x811c9dc5 ^ seed) >>> 0;
    for (let index = 0; index < text.length; index += 1) {
        hash ^= text.charCodeAt(index);
        hash = Math.imul(hash, 0x01000193) >>> 0;
    }
    hash ^= hash >>> 16;
    hash = Math.imul(hash, 0x85ebca6b) >>> 0;
    hash ^= hash >>> 13;
    hash = Math.imul(hash, 0xc2b2ae35) >>> 0;
    hash ^= hash >>> 16;
    return hash >>> 0;
}
/**
 * The name a player is shown under when nothing better applies (§4.2): *Amber Otter 42*.
 *
 * A pure function of the per-game key — an adjective and an animal from two curated lists of 256,
 * then two digits, so 6.5 million names. The same in every place that renders one, because the
 * API, the dashboard, the library and both SDKs' fixtures all call this; the same every time in
 * one game, because the key is; different in every other game, because the key is.
 *
 * **Not an identity.** It never touches `users`, nothing anybody typed is in it, and it is not
 * unique — two players in one game share one about as often as the birthday problem says. It is
 * mixed from the whole key rather than read off its first bytes, so it says nothing about the
 * eight-character prefix a team sees in moderation.
 */
export function pseudonymFor(playerKey) {
    const high = hash32(playerKey, 0);
    const low = hash32(playerKey, 0x9e3779b9);
    const adjective = PSEUDONYM_ADJECTIVES[high & 0xff];
    const animal = PSEUDONYM_ANIMALS[(high >>> 8) & 0xff];
    const digits = String(low % 100).padStart(2, '0');
    return `${adjective} ${animal} ${digits}`;
}
/* ------------------------------------------------------------------------ the honest description */
/**
 * What play services send, said once (§14.2). The channel switches' panel and the Privacy page
 * both render this constant, so they cannot drift apart.
 */
export const PLAY_DISCLOSURE = "With achievements or leaderboards on, this channel's builds send BoardFlight the achievements the " +
    'game awards and the scores it posts, under a random install id, with each Board profile replaced ' +
    'by a one-way code that differs in every game. BoardFlight never receives player names or Board ' +
    "profile ids. A player's scores appear under a generated name unless the player links the game to " +
    'their own BoardFlight account; a public link shows their username and their profile to anyone.';
//# sourceMappingURL=play.js.map